import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

const MAPBOX_TOKEN =
  (import.meta.env.VITE_MAPBOX_ACCESS_TOKEN as string | undefined) ||
  (import.meta.env.VITE_MAPBOX_TOKEN as string | undefined) ||
  '';

function getLastGeoCenter(): { lat: number; lng: number } | null {
  try {
    const raw = localStorage.getItem('lastGeo');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      center?: { lat?: number; lng?: number };
    };
    const { lat, lng } = parsed.center ?? {};
    if (typeof lat === 'number' && typeof lng === 'number') return { lat, lng };
    return null;
  } catch {
    return null;
  }
}

export interface PinOnMapPickerProps {
  coordinates: { lat: number; lng: number };
  onChange: (coords: { lat: number; lng: number }) => void;
}

/**
 * Inline Mapbox GL map with a single draggable marker.
 * Used to let users fine-tune exact event pin placement within an address.
 *
 * - If coordinates are non-zero, centers on them.
 * - Otherwise falls back to `localStorage.lastGeo.center`.
 * - When the marker is dragged, calls `onChange` with the new coordinates.
 * - Responds to external coordinate changes (e.g. address autocomplete) by
 *   flying the map and updating the marker.
 */
export const PinOnMapPicker: React.FC<PinOnMapPickerProps> = ({
  coordinates,
  onChange,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markerRef = useRef<mapboxgl.Marker | null>(null);
  const isDraggingRef = useRef(false);

  const hasCoords = coordinates.lat !== 0 || coordinates.lng !== 0;

  const resolveInitialCenter = (): [number, number] => {
    if (hasCoords) return [coordinates.lng, coordinates.lat];
    const last = getLastGeoCenter();
    if (last) return [last.lng, last.lat];
    return [16.3738, 48.2082]; // Vienna fallback
  };

  // Init map once on mount
  useEffect(() => {
    if (!containerRef.current) return;

    if (!MAPBOX_TOKEN) {
      return;
    }

    mapboxgl.accessToken = MAPBOX_TOKEN;

    const center = resolveInitialCenter();

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center,
      zoom: 15,
      attributionControl: false,
    });

    map.addControl(new mapboxgl.AttributionControl({ compact: true }));

    mapRef.current = map;

    const marker = new mapboxgl.Marker({
      draggable: true,
      color: 'rgb(191, 32, 136)',
    })
      .setLngLat(center)
      .addTo(map);

    markerRef.current = marker;

    marker.on('dragstart', () => {
      isDraggingRef.current = true;
    });

    marker.on('dragend', () => {
      isDraggingRef.current = false;
      const lngLat = marker.getLngLat();
      onChange({ lat: lngLat.lat, lng: lngLat.lng });
    });

    return () => {
      marker.remove();
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync external coordinate changes to the map/marker (e.g. from address autocomplete)
  useEffect(() => {
    if (isDraggingRef.current) return;
    if (!mapRef.current || !markerRef.current) return;
    if (coordinates.lat === 0 && coordinates.lng === 0) return;

    const current = markerRef.current.getLngLat();
    const moved =
      Math.abs(current.lat - coordinates.lat) > 0.000001 ||
      Math.abs(current.lng - coordinates.lng) > 0.000001;

    if (!moved) return;

    mapRef.current.flyTo({
      center: [coordinates.lng, coordinates.lat],
      zoom: 15,
      duration: 600,
    });
    markerRef.current.setLngLat([coordinates.lng, coordinates.lat]);
  }, [coordinates.lat, coordinates.lng]);

  if (!MAPBOX_TOKEN) {
    return (
      <div className="h-[150px] bg-gray-100 rounded-lg flex items-center justify-center text-xs text-gray-400 border border-gray-200">
        Map unavailable (Mapbox token not configured)
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="h-[150px] w-full rounded-lg overflow-hidden border border-gray-200"
    />
  );
};
